import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { 
  Loader2, 
  HardDrive, 
  Download, 
  Trash2, 
  RefreshCw,
  Plus,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Calendar,
  Play,
  Github,
  Upload,
  Pencil,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { 
  getBackups, 
  deleteBackup, 
  downloadBackup, 
  restoreBackup,
  getBackupSchedules,
  createBackupSchedule,
  toggleBackupSchedule,
  runBackupScheduleNow,
  deleteBackupSchedule,
  editBackupSchedule,
  createBackup,
  validateServerStorage,
  startMultipartUpload,
  getPartUploadUrl,
  completeMultipartUpload,
} from '@/api/project';
import type { Backup, BackupDeployment, BackupSchedule } from '@/types/project';

// Status badge component
function StatusBadge({ status }: { status: string }) {
  const lowerStatus = status.toLowerCase();
  
  if (lowerStatus === 'completed' || lowerStatus === 'success') {
    return (
      <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
        <CheckCircle className="h-3 w-3 mr-1" />
        Completed
      </Badge>
    );
  }
  if (lowerStatus === 'running' || lowerStatus === 'in_progress') {
    return (
      <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
        <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
        Running
      </Badge>
    );
  }
  if (lowerStatus === 'failed' || lowerStatus === 'error') {
    return (
      <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
        <XCircle className="h-3 w-3 mr-1" />
        Failed
      </Badge>
    );
  }
  if (lowerStatus === 'pending') {
    return (
      <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">
        <Clock className="h-3 w-3 mr-1" />
        Pending
      </Badge>
    );
  }
  if (lowerStatus === 'deleting') {
    return (
      <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">
        <Trash2 className="h-3 w-3 mr-1 animate-pulse" />
        Deleting
      </Badge>
    );
  }
  return <Badge variant="secondary">{status}</Badge>;
}

// Format file size
function formatSize(sizeMb: number | null): string {
  if (!sizeMb) return '—';
  if (sizeMb >= 1024) {
    return `${(sizeMb / 1024).toFixed(1)} GB`;
  }
  return `${sizeMb.toFixed(1)} MB`;
}

// Format date in user's local timezone with timezone abbreviation
function formatDateWithTimezone(dateString: string | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const formattedDate = date.toLocaleString();
  // Get timezone abbreviation (e.g., "PST", "EST", "UTC")
  const timezone = date.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop() || '';
  return `${formattedDate} ${timezone}`;
}

// Format schedule frequency
function formatFrequency(schedule: BackupSchedule): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  switch (schedule.frequency) {
    case 'hourly':
      return `Every ${schedule.hours_interval || 1} hour(s) at :${pad(schedule.minute)}`;
    case 'daily':
      return `Daily at ${schedule.hour}:${pad(schedule.minute)}`;
    case 'weekly':
      return `Weekly on ${days[schedule.day_of_week || 0]} at ${schedule.hour}:${pad(schedule.minute)}`;
    case 'monthly':
      return `Monthly on day ${schedule.day_of_month} at ${schedule.hour}:${pad(schedule.minute)}`;
    case 'custom':
      return schedule.cron_expression || 'Custom schedule';
    default:
      return schedule.frequency;
  }
}

// Backup row component - compact table row
function BackupRow({ 
  backup, 
  projectId,
  githubRepo,
  deployments,
  onRefresh,
}: { 
  backup: Backup;
  projectId: string;
  githubRepo: string | null;
  deployments: BackupDeployment[];
  onRefresh: () => void;
}) {
  const [isRestoreOpen, setIsRestoreOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [selectedDeployment, setSelectedDeployment] = useState<string>('');
  const [downloadState, setDownloadState] = useState<{
    loading: boolean;
    error: string | null;
    backupType: 'zip' | 'separate_files' | null;
    databaseUrl: string | null;
    filestoreUrl: string | null;
  }>({ loading: false, error: null, backupType: null, databaseUrl: null, filestoreUrl: null });
  
  // Restore dialog state
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [branchConfirmation, setBranchConfirmation] = useState('');
  const [overrideStorageWarning, setOverrideStorageWarning] = useState(false);
  const [storageValidation, setStorageValidation] = useState<{
    loading: boolean;
    error: string | null;
    hasSufficientSpace: boolean | null;
    backupSizeGb: number | null;
    requiredSpaceGb: number | null;
    availableSpaceGb: number | null;
    spaceShortageGb: number | null;
  }>({ loading: false, error: null, hasSufficientSpace: null, backupSizeGb: null, requiredSpaceGb: null, availableSpaceGb: null, spaceShortageGb: null });
  
  const isCompleted = backup.status.toLowerCase() === 'completed';
  const isDeleting = backup.status.toLowerCase() === 'deleting';

  const handleOpenDownload = async () => {
    setIsDownloadOpen(true);
    setDownloadState({ loading: true, error: null, backupType: null, databaseUrl: null, filestoreUrl: null });
    
    try {
      const result = await downloadBackup(projectId, backup.id);
      
      if (result.success) {
        setDownloadState({
          loading: false,
          error: null,
          backupType: result.backup_type || null,
          databaseUrl: result.database_url || null,
          filestoreUrl: result.filestore_url || null,
        });
      } else {
        setDownloadState({
          loading: false,
          error: result.error || 'Failed to generate download links',
          backupType: null,
          databaseUrl: null,
          filestoreUrl: null,
        });
      }
    } catch (err) {
      setDownloadState({
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to generate download links',
        backupType: null,
        databaseUrl: null,
        filestoreUrl: null,
      });
    }
  };
  
  // Handle opening restore dialog - reset state
  const handleOpenRestore = () => {
    setIsRestoreOpen(true);
    setSelectedDeployment('');
    setConfirmRestore(false);
    setBranchConfirmation('');
    setOverrideStorageWarning(false);
    setStorageValidation({ loading: false, error: null, hasSufficientSpace: null, backupSizeGb: null, requiredSpaceGb: null, availableSpaceGb: null, spaceShortageGb: null });
  };
  
  // Validate storage when deployment is selected
  const handleDeploymentSelect = async (deploymentId: string) => {
    setSelectedDeployment(deploymentId);
    setConfirmRestore(false);
    setOverrideStorageWarning(false);
    
    const deployment = deployments.find(d => d.id.toString() === deploymentId);
    if (!deployment?.server_id || !backup.size_mb) {
      setStorageValidation({
        loading: false,
        error: backup.size_mb ? null : 'Could not determine backup size. Please verify server has sufficient storage space manually.',
        hasSufficientSpace: null,
        backupSizeGb: null,
        requiredSpaceGb: null,
        availableSpaceGb: null,
        spaceShortageGb: null,
      });
      return;
    }
    
    setStorageValidation(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await validateServerStorage(deployment.server_id, backup.size_mb);
      
      if (result.error) {
        setStorageValidation({
          loading: false,
          error: result.error,
          hasSufficientSpace: null,
          backupSizeGb: null,
          requiredSpaceGb: null,
          availableSpaceGb: null,
          spaceShortageGb: null,
        });
      } else {
        setStorageValidation({
          loading: false,
          error: null,
          hasSufficientSpace: result.has_sufficient_space ?? null,
          backupSizeGb: result.backup_size_gb ?? null,
          requiredSpaceGb: result.required_space_gb ?? null,
          availableSpaceGb: result.available_space_gb ?? null,
          spaceShortageGb: result.space_shortage_gb ?? null,
        });
      }
    } catch {
      setStorageValidation({
        loading: false,
        error: 'Failed to check server storage. Please verify manually before proceeding.',
        hasSufficientSpace: null,
        backupSizeGb: null,
        requiredSpaceGb: null,
        availableSpaceGb: null,
        spaceShortageGb: null,
      });
    }
  };

  const [isRestoring, setIsRestoring] = useState(false);
  const [isDeletingBackup, setIsDeletingBackup] = useState(false);
  
  // Determine if restore button should be enabled
  const canRestore = () => {
    if (!selectedDeployment || !confirmRestore || isRestoring) return false;
    // Check if branch name confirmation matches
    const deployment = deployments.find(d => d.id.toString() === selectedDeployment);
    if (!deployment || !deployment.branch_name) return false;
    if (branchConfirmation.trim() !== deployment.branch_name.trim()) return false;
    // If storage validation failed but user didn't override, block
    if (storageValidation.hasSufficientSpace === false && !overrideStorageWarning) return false;
    return true;
  };

  const handleRestore = async () => {
    if (!canRestore()) return;
    setIsRestoring(true);
    try {
      const result = await restoreBackup(projectId, backup.id, parseInt(selectedDeployment));
      if (result.success) {
        setIsRestoreOpen(false);
        onRefresh();
      } else {
        toast.error('Failed to restore backup. Please try again.');
      }
    } catch {
      toast.error('Failed to restore backup. Please try again.');
    } finally {
      setIsRestoring(false);
    }
  };

  const handleDelete = async () => {
    setIsDeletingBackup(true);
    try {
      const result = await deleteBackup(projectId, backup.id);
      if (result.success) {
        setIsDeleteOpen(false);
        onRefresh();
      } else {
        toast.error('Failed to delete backup. Please try again.');
      }
    } catch {
      toast.error('Failed to delete backup. Please try again.');
    } finally {
      setIsDeletingBackup(false);
    }
  };

  return (
    <>
      <tr className="border-b hover:bg-gray-50">
        {/* Date */}
        <td className="py-2 px-3 text-sm whitespace-nowrap">
          {formatDateWithTimezone(backup.created_at)}
        </td>
        
        {/* Database */}
        <td className="py-2 px-3 text-sm max-w-[150px] truncate" title={backup.build?.db_name || ''}>
          {backup.build?.db_name || '—'}
        </td>
        
        {/* Odoo Version */}
        <td className="py-2 px-3">
          {backup.odoo_version ? (
            <Badge variant="secondary" className="bg-indigo-100 text-indigo-700">
              Odoo {backup.odoo_version}
            </Badge>
          ) : (
            <span className="text-sm text-gray-400">—</span>
          )}
        </td>
        
        {/* Branch */}
        <td className="py-2 px-3 text-sm">
          {backup.branch?.name || '—'}
        </td>
        
        {/* Commit */}
        <td className="py-2 px-3">
          {backup.commit_hash && githubRepo ? (
            <a 
              href={`https://github.com/${githubRepo}/commit/${backup.commit_hash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center"
            >
              <Badge variant="default" className="bg-primary hover:bg-primary/90">
                <Github className="h-3 w-3 mr-1" />
                #{backup.commit_hash.slice(0, 7)}
              </Badge>
            </a>
          ) : backup.commit_hash ? (
            <span className="text-xs font-mono text-gray-500">
              #{backup.commit_hash.slice(0, 7)}
            </span>
          ) : (
            <span className="text-sm text-gray-400">—</span>
          )}
        </td>
        
        {/* Status */}
        <td className="py-2 px-3">
          <StatusBadge status={backup.status} />
        </td>
        
        {/* Size */}
        <td className="py-2 px-3 text-sm text-gray-600">
          {formatSize(backup.size_mb)}
        </td>
        
        {/* Comments */}
        <td className="py-2 px-3 text-sm text-gray-500 max-w-[150px] truncate" title={backup.comments || ''}>
          {backup.comments || '—'}
        </td>
        
        {/* Actions */}
        <td className="py-2 px-3 text-right">
          <div className="flex items-center justify-end gap-1">
            {isCompleted && (
              <>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleOpenDownload}
                  className="h-7 px-2"
                >
                  <Download className="h-3.5 w-3.5" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleOpenRestore}
                  className="h-7 px-2"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setIsDeleteOpen(true)}
              disabled={isDeleting}
              className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </td>
      </tr>

      {/* Download Dialog */}
      <Dialog open={isDownloadOpen} onOpenChange={setIsDownloadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Download Backup</DialogTitle>
            <DialogDescription>
              {backup.created_at && `Backup from ${formatDateWithTimezone(backup.created_at)}`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {downloadState.loading && (
              <div className="flex flex-col items-center py-6">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
                <p className="text-sm text-gray-500">Generating download links...</p>
              </div>
            )}
            
            {downloadState.error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{downloadState.error}</AlertDescription>
              </Alert>
            )}
            
            {!downloadState.loading && !downloadState.error && downloadState.backupType === 'zip' && (
              <div className="space-y-4">
                <Alert>
                  <Clock className="h-4 w-4" />
                  <AlertDescription>This link is valid for 1 hour.</AlertDescription>
                </Alert>
                <Button asChild className="w-full" size="lg">
                  <a href={downloadState.databaseUrl || '#'} target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4 mr-2" />
                    Download Backup
                  </a>
                </Button>
              </div>
            )}
            
            {!downloadState.loading && !downloadState.error && downloadState.backupType === 'separate_files' && (
              <div className="space-y-4">
                <Alert>
                  <Clock className="h-4 w-4" />
                  <AlertDescription>These links are valid for 1 hour.</AlertDescription>
                </Alert>
                <Alert className="bg-yellow-50 border-yellow-200">
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="text-yellow-800">
                    This backup consists of separate database and filestore files. Download both files to have a complete backup.
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  <Button asChild className="w-full">
                    <a href={downloadState.databaseUrl || '#'} target="_blank" rel="noopener noreferrer">
                      <HardDrive className="h-4 w-4 mr-2" />
                      Download Database Backup
                    </a>
                  </Button>
                  <Button asChild variant="outline" className="w-full">
                    <a href={downloadState.filestoreUrl || '#'} target="_blank" rel="noopener noreferrer">
                      <Download className="h-4 w-4 mr-2" />
                      Download Filestore Backup
                    </a>
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDownloadOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog open={isRestoreOpen} onOpenChange={(open) => !isRestoring && setIsRestoreOpen(open)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Restore Backup</DialogTitle>
            <DialogDescription>
              Restore this backup to a deployment. All current data will be replaced.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Warning Alert */}
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>WARNING: This is a destructive action!</AlertTitle>
              <AlertDescription>
                Restoring this backup will permanently delete all current data on the deployment and replace it with the backup's data.
              </AlertDescription>
            </Alert>
            
            {/* Backup Information */}
            <div>
              <Label className="text-sm font-medium">Backup Information</Label>
              <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Created:</span>{' '}
                    <span className="font-medium">{formatDateWithTimezone(backup.created_at)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Database:</span>{' '}
                    <span className="font-medium">{backup.build?.db_name || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Version:</span>{' '}
                    <span className="font-medium">Odoo {backup.odoo_version || 'Unknown'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Size:</span>{' '}
                    <span className="font-medium">{formatSize(backup.size_mb)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Branch:</span>{' '}
                    <span className="font-medium">{backup.branch?.name || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Comments:</span>{' '}
                    <span className="font-medium">{backup.comments || 'None'}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Target Deployment */}
            <div>
              <Label className="text-sm font-medium">Target Deployment</Label>
              <Select value={selectedDeployment} onValueChange={handleDeploymentSelect} disabled={isRestoring}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select deployment to restore to..." />
                </SelectTrigger>
                <SelectContent>
                  {deployments.map((dep) => (
                    <SelectItem key={dep.id} value={dep.id.toString()}>
                      {dep.branch_name || dep.fqdn || `Deployment ${dep.id}`} ({dep.type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Selected deployment details */}
              {selectedDeployment && (() => {
                const dep = deployments.find(d => d.id.toString() === selectedDeployment);
                return dep ? (
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">FQDN:</span>{' '}
                        <span className="font-medium">{dep.fqdn || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Database:</span>{' '}
                        <span className="font-medium">{dep.db_name || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Odoo Version:</span>{' '}
                        <span className="font-medium">{dep.odoo_version || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Server:</span>{' '}
                        <span className="font-medium">{dep.server_name || 'Unknown'}</span>
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
            
            {/* Storage Validation */}
            {selectedDeployment && (
              <div>
                <Label className="text-sm font-medium">Storage Requirements</Label>
                <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
                  {storageValidation.loading ? (
                    <div className="flex items-center justify-center py-2">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      <span className="text-sm text-gray-500">Checking server storage...</span>
                    </div>
                  ) : storageValidation.error ? (
                    <div className="flex items-center text-yellow-600">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      <span className="text-sm">{storageValidation.error}</span>
                    </div>
                  ) : storageValidation.hasSufficientSpace !== null ? (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Backup Size:</span>{' '}
                        <span className="font-medium">{storageValidation.backupSizeGb} GB</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Required Space:</span>{' '}
                        <span className="font-medium">{storageValidation.requiredSpaceGb} GB</span>
                        <span className="text-xs text-gray-400 ml-1">(2x for extraction)</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Available Space:</span>{' '}
                        <span className="font-medium">{storageValidation.availableSpaceGb} GB</span>
                      </div>
                      <div>
                        {storageValidation.hasSufficientSpace ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Sufficient Space
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Insufficient Space
                          </Badge>
                        )}
                      </div>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">Storage validation unavailable</span>
                  )}
                </div>
              </div>
            )}
            
            {/* Insufficient Storage Warning */}
            {storageValidation.hasSufficientSpace === false && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Insufficient Storage Space!</AlertTitle>
                <AlertDescription className="space-y-2">
                  <p>
                    The server needs an additional {storageValidation.spaceShortageGb} GB of free space to restore this backup.
                    Please free up disk space or choose a different server before proceeding.
                  </p>
                  <div className="flex items-center space-x-2 mt-2">
                    <input
                      type="checkbox"
                      id="overrideStorage"
                      checked={overrideStorageWarning}
                      onChange={(e) => setOverrideStorageWarning(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <label htmlFor="overrideStorage" className="text-sm font-medium">
                      I understand the risks and want to proceed anyway
                    </label>
                  </div>
                </AlertDescription>
              </Alert>
            )}
            
            {/* Branch Name Confirmation */}
            {selectedDeployment && (
              <div className="space-y-2">
                <Label htmlFor="branchConfirm" className="text-sm font-medium">
                  Type the branch name to confirm
                </Label>
                <div className="space-y-1">
                  <Input
                    id="branchConfirm"
                    type="text"
                    value={branchConfirmation}
                    onChange={(e) => setBranchConfirmation(e.target.value)}
                    placeholder={`Type "${deployments.find(d => d.id.toString() === selectedDeployment)?.branch_name || ''}" to confirm`}
                    disabled={isRestoring}
                    className={branchConfirmation && branchConfirmation.trim() !== (deployments.find(d => d.id.toString() === selectedDeployment)?.branch_name || '').trim() ? 'border-red-500' : ''}
                  />
                  {branchConfirmation && branchConfirmation.trim() !== (deployments.find(d => d.id.toString() === selectedDeployment)?.branch_name || '').trim() && (
                    <p className="text-xs text-red-600">Branch name does not match</p>
                  )}
                </div>
              </div>
            )}
            
            {/* Confirmation Checkbox */}
            <div className="flex items-center space-x-2 pt-2 border-t">
              <input
                type="checkbox"
                id="confirmRestore"
                checked={confirmRestore}
                onChange={(e) => setConfirmRestore(e.target.checked)}
                disabled={isRestoring}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="confirmRestore" className="text-sm">
                I understand that all current data will be permanently deleted and replaced with the backup data.
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRestoreOpen(false)} disabled={isRestoring}>
              Cancel
            </Button>
            <Button 
              variant={storageValidation.hasSufficientSpace === false && overrideStorageWarning ? 'outline' : 'destructive'}
              onClick={handleRestore} 
              disabled={!canRestore()}
              className={storageValidation.hasSufficientSpace === false && overrideStorageWarning ? 'border-yellow-500 text-yellow-600 hover:bg-yellow-50' : ''}
            >
              {isRestoring ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Restoring...
                </>
              ) : storageValidation.hasSufficientSpace === false ? (
                overrideStorageWarning ? 'Restore Backup (Override Warning)' : 'Insufficient Storage'
              ) : (
                'Restore Backup'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={(open) => !isDeletingBackup && setIsDeleteOpen(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Backup</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this backup? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)} disabled={isDeletingBackup}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeletingBackup}>
              {isDeletingBackup ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Schedule row component
function ScheduleRow({
  schedule,
  projectId,
  onRefresh,
}: {
  schedule: BackupSchedule;
  projectId: string;
  onRefresh: () => void;
}) {
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [deleteBackups, setDeleteBackups] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  const handleToggle = async (active: boolean) => {
    try {
      await toggleBackupSchedule(projectId, schedule.id, active);
      onRefresh();
    } catch {
      toast.error('Failed to toggle schedule. Please try again.');
    }
  };

  const handleRunNow = async () => {
    setIsRunning(true);
    try {
      const result = await runBackupScheduleNow(projectId, schedule.id);
      if (result.success) {
        toast.success('Backup started successfully');
        onRefresh();
      } else {
        toast.error('Failed to start backup. Please try again.');
      }
    } catch {
      toast.error('Failed to start backup. Please try again.');
    } finally {
      setIsRunning(false);
    }
  };

  const handleDelete = async () => {
    try {
      const result = await deleteBackupSchedule(projectId, schedule.id, deleteBackups);
      if (result.success) {
        setIsDeleteOpen(false);
        onRefresh();
      } else {
        toast.error('Failed to delete schedule. Please try again.');
      }
    } catch {
      toast.error('Failed to delete schedule. Please try again.');
    }
  };

  return (
    <>
      <tr className="border-b hover:bg-gray-50">
        {/* Name */}
        <td className="py-2 px-3 text-sm font-medium">{schedule.name}</td>
        
        {/* Branch */}
        <td className="py-2 px-3 text-sm">
          {schedule.branch_name || <span className="text-red-500">Build not found</span>}
        </td>
        
        {/* Frequency */}
        <td className="py-2 px-3 text-sm">{formatFrequency(schedule)}</td>
        
        {/* Next Run */}
        <td className="py-2 px-3 text-sm text-gray-600 whitespace-nowrap">
          {formatDateWithTimezone(schedule.next_run)}
        </td>
        
        {/* Last Run */}
        <td className="py-2 px-3 text-sm text-gray-600 whitespace-nowrap">
          {schedule.last_run ? formatDateWithTimezone(schedule.last_run) : 'Never'}
        </td>
        
        {/* Retention */}
        <td className="py-2 px-3 text-sm">{schedule.retention_count} backups</td>
        
        {/* Status */}
        <td className="py-2 px-3">
          <div className="flex items-center gap-2">
            <Switch 
              checked={schedule.active} 
              onCheckedChange={handleToggle}
            />
            <span className={cn(
              "text-sm",
              schedule.active ? "text-green-600" : "text-gray-500"
            )}>
              {schedule.active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </td>
        
        {/* Actions */}
        <td className="py-2 px-3 text-right">
          <div className="flex items-center justify-end gap-1">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setIsEditOpen(true)}
              className="h-7 px-2"
              title="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRunNow}
              disabled={isRunning}
              className="h-7 px-2"
              title="Run Now"
            >
              {isRunning ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Play className="h-3.5 w-3.5" />
              )}
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setIsDeleteOpen(true)}
              className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </td>
      </tr>

      {/* Edit Dialog */}
      <EditScheduleModal
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        projectId={projectId}
        schedule={schedule}
        onSuccess={onRefresh}
      />

      {/* Delete Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Schedule "{schedule.name}"</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this backup schedule?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="flex items-center gap-2">
              <input 
                type="checkbox" 
                checked={deleteBackups}
                onChange={(e) => setDeleteBackups(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm">Also delete all backups created by this schedule</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Create Backup Modal
function CreateBackupModal({
  open,
  onOpenChange,
  projectId,
  deployments,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  deployments: BackupDeployment[];
  onSuccess: () => void;
}) {
  const [selectedDeployment, setSelectedDeployment] = useState<string>('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedDeployment) return;
    setIsSubmitting(true);
    try {
      const result = await createBackup(projectId, parseInt(selectedDeployment), comment);
      if (result.success) {
        onOpenChange(false);
        setSelectedDeployment('');
        setComment('');
        onSuccess();
      } else {
        toast.error('Failed to create backup. Please try again.');
      }
    } catch {
      toast.error('Failed to create backup. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Backup</DialogTitle>
          <DialogDescription>
            Create a new backup of a production or staging deployment.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Deployment</Label>
            <Select value={selectedDeployment} onValueChange={setSelectedDeployment}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select deployment..." />
              </SelectTrigger>
              <SelectContent>
                {deployments.map((dep) => (
                  <SelectItem key={dep.id} value={dep.id.toString()}>
                    {dep.branch_name || dep.fqdn || `Deployment ${dep.id}`} ({dep.type})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Comment (optional)</Label>
            <Textarea 
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a note about this backup..."
              className="mt-2"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!selectedDeployment || isSubmitting}>
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Backup
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Create Schedule Modal
function CreateScheduleModal({
  open,
  onOpenChange,
  projectId,
  deployments,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  deployments: BackupDeployment[];
  onSuccess: () => void;
}) {
  const [name, setName] = useState('');
  const [selectedDeployment, setSelectedDeployment] = useState<string>('');
  const [frequency, setFrequency] = useState<string>('daily');
  const [retentionCount, setRetentionCount] = useState(7);
  const [comment, setComment] = useState('');
  const [hoursInterval, setHoursInterval] = useState(1);
  const [minute, setMinute] = useState(0);
  const [dailyTime, setDailyTime] = useState('03:00');
  const [weeklyTime, setWeeklyTime] = useState('03:00');
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [monthlyTime, setMonthlyTime] = useState('03:00');
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name || !selectedDeployment) return;
    setIsSubmitting(true);
    try {
      const result = await createBackupSchedule(projectId, {
        name,
        build_id: parseInt(selectedDeployment),
        frequency,
        retention_count: retentionCount,
        comments: comment || undefined,
        hours_interval: hoursInterval,
        minute,
        daily_time: dailyTime,
        weekly_time: weeklyTime,
        day_of_week: dayOfWeek,
        monthly_time: monthlyTime,
        day_of_month: dayOfMonth,
      });
      if (result.success) {
        onOpenChange(false);
        resetForm();
        onSuccess();
      } else {
        toast.error('Failed to create schedule. Please try again.');
      }
    } catch {
      toast.error('Failed to create schedule. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setName('');
    setSelectedDeployment('');
    setFrequency('daily');
    setRetentionCount(7);
    setComment('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Backup Schedule</DialogTitle>
          <DialogDescription>
            Set up automated backups for your deployment.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          <div>
            <Label>Schedule Name</Label>
            <Input 
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Daily Production Backup"
              className="mt-2"
            />
          </div>
          
          <div>
            <Label>Deployment</Label>
            <Select value={selectedDeployment} onValueChange={setSelectedDeployment}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select deployment..." />
              </SelectTrigger>
              <SelectContent>
                {deployments.map((dep) => (
                  <SelectItem key={dep.id} value={dep.id.toString()}>
                    {dep.branch_name || dep.fqdn || `Deployment ${dep.id}`} ({dep.type})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Frequency</Label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Frequency-specific options */}
          {frequency === 'hourly' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Every X Hours</Label>
                <Select value={hoursInterval.toString()} onValueChange={(v) => setHoursInterval(parseInt(v))}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 6, 8, 12].map((h) => (
                      <SelectItem key={h} value={h.toString()}>{h} hour(s)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>At Minute</Label>
                <Select value={minute.toString()} onValueChange={(v) => setMinute(parseInt(v))}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[0, 15, 30, 45].map((m) => (
                      <SelectItem key={m} value={m.toString()}>{m.toString().padStart(2, '0')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {frequency === 'daily' && (
            <div>
              <Label>Time</Label>
              <Input 
                type="time" 
                value={dailyTime}
                onChange={(e) => setDailyTime(e.target.value)}
                className="mt-2"
              />
            </div>
          )}

          {frequency === 'weekly' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Day of Week</Label>
                <Select value={dayOfWeek.toString()} onValueChange={(v) => setDayOfWeek(parseInt(v))}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, i) => (
                      <SelectItem key={i} value={i.toString()}>{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Time</Label>
                <Input 
                  type="time" 
                  value={weeklyTime}
                  onChange={(e) => setWeeklyTime(e.target.value)}
                  className="mt-2"
                />
              </div>
            </div>
          )}

          {frequency === 'monthly' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Day of Month</Label>
                <Select value={dayOfMonth.toString()} onValueChange={(v) => setDayOfMonth(parseInt(v))}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                      <SelectItem key={d} value={d.toString()}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Time</Label>
                <Input 
                  type="time" 
                  value={monthlyTime}
                  onChange={(e) => setMonthlyTime(e.target.value)}
                  className="mt-2"
                />
              </div>
            </div>
          )}

          <div>
            <Label>Retention (backups to keep)</Label>
            <Select value={retentionCount.toString()} onValueChange={(v) => setRetentionCount(parseInt(v))}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[3, 5, 7, 10, 14, 30].map((n) => (
                  <SelectItem key={n} value={n.toString()}>{n} backups</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Comment (optional)</Label>
            <Textarea 
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a note about this schedule..."
              className="mt-2"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!name || !selectedDeployment || isSubmitting}>
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditScheduleModal({
  open,
  onOpenChange,
  projectId,
  schedule,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  schedule: BackupSchedule;
  onSuccess: () => void;
}) {
  const [name, setName] = useState(schedule.name);
  const [frequency, setFrequency] = useState<string>(schedule.frequency);
  const [retentionCount, setRetentionCount] = useState(schedule.retention_count);
  const [comment, setComment] = useState(schedule.comments || '');
  const [hoursInterval, setHoursInterval] = useState(schedule.hours_interval || 1);
  const [minute, setMinute] = useState(schedule.minute || 0);
  const [dailyTime, setDailyTime] = useState(
    schedule.frequency === 'daily' && schedule.hour !== null
      ? `${schedule.hour.toString().padStart(2, '0')}:${(schedule.minute || 0).toString().padStart(2, '0')}`
      : '03:00'
  );
  const [weeklyTime, setWeeklyTime] = useState(
    schedule.frequency === 'weekly' && schedule.hour !== null
      ? `${schedule.hour.toString().padStart(2, '0')}:${(schedule.minute || 0).toString().padStart(2, '0')}`
      : '03:00'
  );
  const [dayOfWeek, setDayOfWeek] = useState(schedule.day_of_week || 0);
  const [monthlyTime, setMonthlyTime] = useState(
    schedule.frequency === 'monthly' && schedule.hour !== null
      ? `${schedule.hour.toString().padStart(2, '0')}:${(schedule.minute || 0).toString().padStart(2, '0')}`
      : '03:00'
  );
  const [dayOfMonth, setDayOfMonth] = useState(schedule.day_of_month || 1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name) return;
    setIsSubmitting(true);
    try {
      const result = await editBackupSchedule(projectId, schedule.id, {
        name,
        frequency,
        retention_count: retentionCount,
        comments: comment || undefined,
        hours_interval: hoursInterval,
        minute,
        daily_time: dailyTime,
        weekly_time: weeklyTime,
        day_of_week: dayOfWeek,
        monthly_time: monthlyTime,
        day_of_month: dayOfMonth,
      });
      if (result.success) {
        onOpenChange(false);
        onSuccess();
        toast.success('Schedule updated successfully');
      } else {
        toast.error(result.error || 'Failed to update schedule. Please try again.');
      }
    } catch {
      toast.error('Failed to update schedule. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Backup Schedule</DialogTitle>
          <DialogDescription>
            Modify the settings for this backup schedule.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          <div>
            <Label>Schedule Name</Label>
            <Input 
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Daily Production Backup"
              className="mt-2"
            />
          </div>
          
          <div>
            <Label>Deployment</Label>
            <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
              <span className="text-sm text-gray-600">
                {schedule.branch_name || `Build ${schedule.build_id}`}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Deployment cannot be changed. Create a new schedule if needed.</p>
          </div>

          <div>
            <Label>Frequency</Label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {frequency === 'hourly' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Every X Hours</Label>
                <Select value={hoursInterval.toString()} onValueChange={(v) => setHoursInterval(parseInt(v))}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 6, 8, 12].map((h) => (
                      <SelectItem key={h} value={h.toString()}>{h} hour(s)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>At Minute</Label>
                <Select value={minute.toString()} onValueChange={(v) => setMinute(parseInt(v))}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[0, 15, 30, 45].map((m) => (
                      <SelectItem key={m} value={m.toString()}>{m.toString().padStart(2, '0')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {frequency === 'daily' && (
            <div>
              <Label>Time</Label>
              <Input 
                type="time" 
                value={dailyTime}
                onChange={(e) => setDailyTime(e.target.value)}
                className="mt-2"
              />
            </div>
          )}

          {frequency === 'weekly' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Day of Week</Label>
                <Select value={dayOfWeek.toString()} onValueChange={(v) => setDayOfWeek(parseInt(v))}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, i) => (
                      <SelectItem key={i} value={i.toString()}>{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Time</Label>
                <Input 
                  type="time" 
                  value={weeklyTime}
                  onChange={(e) => setWeeklyTime(e.target.value)}
                  className="mt-2"
                />
              </div>
            </div>
          )}

          {frequency === 'monthly' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Day of Month</Label>
                <Select value={dayOfMonth.toString()} onValueChange={(v) => setDayOfMonth(parseInt(v))}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                      <SelectItem key={d} value={d.toString()}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Time</Label>
                <Input 
                  type="time" 
                  value={monthlyTime}
                  onChange={(e) => setMonthlyTime(e.target.value)}
                  className="mt-2"
                />
              </div>
            </div>
          )}

          <div>
            <Label>Retention (backups to keep)</Label>
            <Select value={retentionCount.toString()} onValueChange={(v) => setRetentionCount(parseInt(v))}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[3, 5, 7, 10, 14, 30].map((n) => (
                  <SelectItem key={n} value={n.toString()}>{n} backups</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Comment (optional)</Label>
            <Textarea 
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a note about this schedule..."
              className="mt-2"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!name || isSubmitting}>
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Upload Backup Modal
function UploadBackupModal({
  open,
  onOpenChange,
  projectId,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  onSuccess: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [comment, setComment] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [uploadSpeed, setUploadSpeed] = useState<string>('');
  const [uploadedBytes, setUploadedBytes] = useState(0);

  const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB chunks for multipart upload (Cloudflare Worker limit: 128MB)
  const MAX_CONCURRENT = 4; // Maximum concurrent uploads

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      // Validate file type
      if (!selectedFile.name.toLowerCase().endsWith('.zip')) {
        setError('Please select a ZIP file containing your backup');
        return;
      }
      // Validate file size (max 50GB)
      if (selectedFile.size > 50 * 1024 * 1024 * 1024) {
        setError('File size exceeds the 50GB limit. Please use a smaller backup file.');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const formatSpeed = (bytesPerSecond: number): string => {
    const mbPerSecond = bytesPerSecond / (1024 * 1024);
    return `${mbPerSecond.toFixed(1)} MB/s`;
  };


  const uploadPart = async (
    file: File,
    partNumber: number,
    uploadId: string,
    bucket: string,
    key: string
  ): Promise<{ ETag: string; PartNumber: number }> => {
    // Get presigned URL for this part
    const urlResponse = await getPartUploadUrl(
      projectId,
      uploadId,
      partNumber,
      bucket,
      key
    );

    if (!urlResponse.success || !urlResponse.url) {
      const error = urlResponse.error || 'Failed to get upload URL';
      if (error.includes('expired')) {
        throw new Error('Upload session expired');
      }
      throw new Error('Network error occurred');
    }

    // Calculate the slice of the file for this part
    const start = (partNumber - 1) * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);

    // Upload the chunk directly to the presigned URL
    const uploadResponse = await fetch(urlResponse.url, {
      method: 'PUT',
      body: chunk,
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload part ${partNumber}`);
    }

    // Get the ETag from the response headers
    const etag = uploadResponse.headers.get('ETag') || '';
    return { ETag: etag, PartNumber: partNumber };
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatus('Preparing upload...');
    setError(null);
    setUploadedBytes(0);
    const startTime = Date.now();

    try {
      // Start multipart upload
      setUploadStatus('Connecting to server...');
      const startResponse = await startMultipartUpload(projectId, comment || undefined);
      
      if (!startResponse.success) {
        // User-friendly error messages
        const errorMsg = startResponse.error || 'Failed to start upload';
        if (errorMsg.includes('B2') || errorMsg.includes('credentials')) {
          throw new Error('Backup storage is not configured for this project. Please contact your administrator.');
        } else if (errorMsg.includes('subscription')) {
          throw new Error('An active subscription with backup storage addon is required to upload backups.');
        } else {
          throw new Error('Unable to start upload. Please try again or contact support.');
        }
      }

      const { upload_id, bucket, key, backup_id } = startResponse;
      if (!upload_id || !bucket || !key || !backup_id) {
        throw new Error('Server configuration error. Please contact support.');
      }

      // Calculate number of parts
      const numParts = Math.ceil(file.size / CHUNK_SIZE);
      const parts: Array<{ ETag: string; PartNumber: number }> = [];

      // Upload parts using a streaming queue (constant concurrency)
      setUploadStatus('Uploading...');
      
      // Streaming upload queue - always keeps MAX_CONCURRENT uploads running
      let nextPartNumber = 1;
      let partsCompleted = 0;
      const activeParts = new Set<number>();
      
      const uploadNextPart = async (): Promise<{ ETag: string; PartNumber: number } | null> => {
        if (nextPartNumber > numParts) return null;
        
        const partNumber = nextPartNumber++;
        activeParts.add(partNumber);
        
        try {
          const part = await uploadPart(file, partNumber, upload_id, bucket, key);
          
          // Calculate bytes for this part
          const start = (partNumber - 1) * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const partBytes = end - start;
          
          partsCompleted++;
          activeParts.delete(partNumber);
          
          // Update state
          setUploadedBytes(prev => {
            const newTotal = prev + partBytes;
            const now = Date.now();
            const elapsedSeconds = (now - startTime) / 1000;
            if (elapsedSeconds > 0) {
              const speed = newTotal / elapsedSeconds;
              setUploadSpeed(formatSpeed(speed));
            }
            return newTotal;
          });
          
          setUploadProgress(Math.min(Math.round((partsCompleted / numParts) * 100), 99));
          
          return part;
        } catch (err) {
          activeParts.delete(partNumber);
          const errorMsg = err instanceof Error ? err.message : 'Network error';
          if (errorMsg.includes('presigned URL')) {
            throw new Error('Upload session expired. Please try again.');
          } else if (errorMsg.includes('Network')) {
            throw new Error('Network connection lost. Please check your internet connection and try again.');
          } else {
            throw new Error(`Upload failed. Please try again.`);
          }
        }
      };
      
      // Worker that continuously uploads parts
      const uploadWorker = async (): Promise<Array<{ ETag: string; PartNumber: number }>> => {
        const workerParts: Array<{ ETag: string; PartNumber: number }> = [];
        while (nextPartNumber <= numParts || activeParts.size > 0) {
          const part = await uploadNextPart();
          if (part) {
            workerParts.push(part);
          } else if (activeParts.size === 0) {
            break;
          }
        }
        return workerParts;
      };
      
      // Start workers
      const workers = Array(Math.min(MAX_CONCURRENT, numParts)).fill(null).map(() => uploadWorker());
      const workerResults = await Promise.all(workers);
      workerResults.forEach(workerParts => parts.push(...workerParts));

      // Sort parts by part number (important for completion)
      parts.sort((a, b) => a.PartNumber - b.PartNumber);

      // Complete multipart upload
      setUploadStatus('Finalizing...');
      setUploadSpeed('');
      const completeResponse = await completeMultipartUpload(
        projectId,
        upload_id,
        parts,
        bucket,
        key,
        backup_id,
        file.size
      );

      if (!completeResponse.success) {
        throw new Error('Failed to finalize upload. The backup may be incomplete.');
      }

      // Success!
      toast.success('Backup uploaded successfully!');
      onOpenChange(false);
      setFile(null);
      setComment('');
      setUploadSpeed('');
      onSuccess();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred during upload.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
      setUploadSpeed('');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !isUploading && onOpenChange(open)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Backup</DialogTitle>
          <DialogDescription>
            Upload a backup ZIP file to restore later. Maximum file size: 50GB.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* File Selection */}
          <div>
            <Label>Backup File</Label>
            <div className="mt-2 space-y-2">
              <Input
                type="file"
                accept=".zip"
                onChange={handleFileSelect}
                disabled={isUploading}
                className="cursor-pointer"
              />
              {file && (
                <div className="text-sm text-muted-foreground">
                  Selected: {file.name} ({formatFileSize(file.size)})
                </div>
              )}
            </div>
          </div>

          {/* Comment */}
          <div>
            <Label>Comment (optional)</Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a note about this backup..."
              className="mt-2"
              disabled={isUploading}
            />
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{uploadStatus}</span>
                <div className="flex items-center gap-3">
                  {uploadSpeed && (
                    <span className="text-muted-foreground">{uploadSpeed}</span>
                  )}
                  <span className="font-medium">{uploadProgress}%</span>
                </div>
              </div>
              <Progress value={uploadProgress} className="h-2" />
              {uploadedBytes > 0 && (
                <div className="text-xs text-muted-foreground text-center">
                  {formatFileSize(uploadedBytes)} of {formatFileSize(file?.size || 0)}
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Info Alert */}
          {!isUploading && !error && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Your backup will be securely stored and can be restored to any deployment in this project.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isUploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={!file || isUploading}>
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload Backup
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BackupsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState('backups');
  const [isCreateBackupOpen, setIsCreateBackupOpen] = useState(false);
  const [isCreateScheduleOpen, setIsCreateScheduleOpen] = useState(false);
  const [isUploadBackupOpen, setIsUploadBackupOpen] = useState(false);
  
  // Fetch backups
  const { data: backupsData, isLoading: backupsLoading, error: backupsError, refetch: refetchBackups } = useQuery({
    queryKey: ['backups', projectId, page],
    queryFn: () => getBackups(projectId!, page),
    enabled: !!projectId,
  });

  // Fetch schedules
  const { data: schedulesData, isLoading: schedulesLoading, refetch: refetchSchedules } = useQuery({
    queryKey: ['backup-schedules', projectId],
    queryFn: () => getBackupSchedules(projectId!),
    enabled: !!projectId,
  });

  // WebSocket for real-time backup status updates
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!projectId) return;

    // Connect to the socket
    const socket = io('/socket.io', {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      // Join the project room
      socket.emit('join_project', { project_id: projectId });
    });

    // Listen for backup status updates
    socket.on('backup_status_update', () => {
      refetchBackups();
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [projectId, refetchBackups]);

  // Get github repo from project context (we'd need to fetch this from somewhere)
  // For now we'll pass null and the commit links won't work
  const githubRepo: string | null = null; // TODO: Get from project data

  const handleRefresh = () => {
    refetchBackups();
    refetchSchedules();
  };

  if (backupsLoading && schedulesLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (backupsError) {
    return (
      <DashboardLayout>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Failed to load backups. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      </DashboardLayout>
    );
  }

  const backups = backupsData?.backups || [];
  const pagination = backupsData?.pagination;
  const deployments = backupsData?.deployments || schedulesData?.deployments || [];
  const schedules = schedulesData?.schedules || [];

  return (
    <DashboardLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <HardDrive className="h-6 w-6" />
              Backups
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Manage database backups and schedules
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="backups" className="gap-1.5">
                <HardDrive className="h-4 w-4" />
                Backups
              </TabsTrigger>
              <TabsTrigger value="scheduler" className="gap-1.5">
                <Calendar className="h-4 w-4" />
                Backup Scheduler
              </TabsTrigger>
            </TabsList>
            
            {/* Action buttons */}
            <div className="flex gap-2">
              {activeTab === 'backups' && (
                <>
                  <Button onClick={() => setIsUploadBackupOpen(true)}>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Backup
                  </Button>
                  <Button onClick={() => setIsCreateBackupOpen(true)} disabled={deployments.length === 0}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Backup
                  </Button>
                </>
              )}
              {activeTab === 'scheduler' && (
                <Button onClick={() => setIsCreateScheduleOpen(true)} disabled={deployments.length === 0}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Schedule
                </Button>
              )}
            </div>
          </div>

          {/* Backups Tab */}
          <TabsContent value="backups" className="mt-4">
            <Card>
              <CardHeader className="py-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">All Backups</CardTitle>
                  <CardDescription>{pagination?.total || 0} total</CardDescription>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {backups.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <HardDrive className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium mb-2">No backups yet</p>
                    <p className="text-sm">Create your first backup to get started.</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-y">
                          <tr>
                            <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Database</th>
                            <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Odoo</th>
                            <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Branch</th>
                            <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Commit</th>
                            <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                            <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Comments</th>
                            <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {backups.map((backup) => (
                            <BackupRow 
                              key={backup.id} 
                              backup={backup}
                              projectId={projectId!}
                              githubRepo={githubRepo}
                              deployments={deployments}
                              onRefresh={handleRefresh}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination */}
                    {pagination && pagination.pages > 1 && (
                      <div className="flex items-center justify-center gap-2 py-4 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={!pagination.has_prev}
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          Previous
                        </Button>
                        <span className="text-sm text-gray-500">
                          Page {pagination.page} of {pagination.pages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage(p => p + 1)}
                          disabled={!pagination.has_next}
                        >
                          Next
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scheduler Tab */}
          <TabsContent value="scheduler" className="mt-4">
            <Card>
              <CardHeader className="py-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Backup Schedules</CardTitle>
                  <CardDescription>{schedules.length} schedule(s)</CardDescription>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {schedulesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : schedules.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium mb-2">No backup schedules yet</p>
                    <p className="text-sm mb-4">Create your first schedule to automate your backups.</p>
                    <Button onClick={() => setIsCreateScheduleOpen(true)} disabled={deployments.length === 0}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Backup Schedule
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-y">
                        <tr>
                          <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                          <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Branch</th>
                          <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
                          <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Next Run</th>
                          <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Last Run</th>
                          <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Retention</th>
                          <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {schedules.map((schedule) => (
                          <ScheduleRow 
                            key={schedule.id} 
                            schedule={schedule}
                            projectId={projectId!}
                            onRefresh={handleRefresh}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modals */}
      <CreateBackupModal
        open={isCreateBackupOpen}
        onOpenChange={setIsCreateBackupOpen}
        projectId={projectId!}
        deployments={deployments}
        onSuccess={handleRefresh}
      />
      
      <CreateScheduleModal
        open={isCreateScheduleOpen}
        onOpenChange={setIsCreateScheduleOpen}
        projectId={projectId!}
        deployments={deployments}
        onSuccess={handleRefresh}
      />
      
      <UploadBackupModal
        open={isUploadBackupOpen}
        onOpenChange={setIsUploadBackupOpen}
        projectId={projectId!}
        onSuccess={handleRefresh}
      />
    </DashboardLayout>
  );
}
